import React from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Check";
import ScorePicker from "./ScorePicker";
import { STATUS_STYLE } from "../constants";

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.inactive;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: "6px",
        fontSize: "0.65rem",
        fontWeight: 600,
        color: s.color,
        bgcolor: s.bg,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </Box>
  );
}

export default function TechnicianEvaluationCard({
  tech,
  row,
  criteria,
  onChange,
  onSave,
  saving,
}) {
  const isInactive = tech.status === "inactive";
  const hasScore = criteria.some((c) => (row[c.field] ?? 0) > 0);

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: "14px",
        bgcolor: "#fff",
        border: "1px solid",
        borderColor: hasScore ? "#bbdefb" : "#e8eaef",
        opacity: isInactive ? 0.72 : 1,
        transition: "border-color 0.2s, box-shadow 0.2s",
        "&:hover": { borderColor: "#90caf9", boxShadow: "0 2px 12px rgba(25,117,209,0.06)" },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "#111827", letterSpacing: "-0.01em" }}
          >
            {tech.name}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <StatusBadge status={tech.status} />
            {tech.zone && (
              <Typography variant="caption" sx={{ color: "#9ca3af" }}>{tech.zone}</Typography>
            )}
          </Stack>
        </Box>
        <Tooltip title="Guardar evaluación">
          <span>
            <IconButton
              size="small"
              onClick={() => onSave(tech._id)}
              disabled={saving}
              sx={{
                bgcolor: "#1975d1",
                color: "#fff",
                width: 36,
                height: 36,
                "&:hover": { bgcolor: "#1565c0" },
                "&.Mui-disabled": { bgcolor: "#e3f2fd", color: "#90caf9" },
              }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Stack spacing={2}>
        {criteria.map((c) => (
          <ScorePicker
            key={c.id}
            label={c.label}
            value={row[c.field] ?? 0}
            onChange={(v) => onChange(tech._id, c.field, v)}
            compact
            accent={c.color}
          />
        ))}
        <TextField
          size="small"
          fullWidth
          placeholder="Observaciones (opcional)"
          value={row.notes ?? ""}
          onChange={(e) => onChange(tech._id, "notes", e.target.value)}
          multiline
          minRows={1}
          maxRows={3}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "0.875rem",
              "& fieldset": { borderColor: "#e8eaef" },
            },
          }}
        />
      </Stack>
    </Box>
  );
}
